import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import { QRCodeSVG } from "qrcode.react";
import { Copy, Check, ChevronRight, Clock, AlertTriangle } from "lucide-react";
import { useAuth } from "../lib/auth";
import { GET_CRYPTO_PRICING, MAKE_CRYPTO_DEPOSIT, GET_USER_WALLETS } from "../graphql/crypto";
import { GET_DASHBOARD_DATA } from "../graphql/dashboard";
import { hapticFeedback, enableBackButton, showConfirm } from "../lib/telegram";
import { GradientHeader } from "../components/GradientHeader";
import { SectionCard } from "../components/SectionCard";
import { PageContainer } from "../components/PageContainer";

type CryptoType = "BTC-USD" | "ETH-USD" | "SOL-USD" | "USDC-USD" | "USDT-USD" | "TRX-USD";
type NetworkType = "mainnet" | "solana" | "tron";
type Step = "currency" | "network" | "card" | "amount" | "confirm";

const CURRENCIES: { symbol: CryptoType; label: string; icon: string }[] = [
  { symbol: "BTC-USD", label: "Bitcoin", icon: "BTC" },
  { symbol: "ETH-USD", label: "Ethereum", icon: "ETH" },
  { symbol: "SOL-USD", label: "Solana", icon: "SOL" },
  { symbol: "TRX-USD", label: "Tron", icon: "TRX" },
  { symbol: "USDC-USD", label: "USD Coin", icon: "USDC" },
  { symbol: "USDT-USD", label: "Tether", icon: "USDT" },
];

const NETWORKS: { value: NetworkType; label: string; description: string }[] = [
  { value: "mainnet", label: "Ethereum", description: "Ethereum Mainnet" },
  { value: "solana", label: "Solana", description: "Solana Network" },
  { value: "tron", label: "Tron", description: "Tron Network" },
];

const MINIMUM_DEPOSIT_USD = 25;

function symbolToChain(symbol: CryptoType, network?: NetworkType): string {
  switch (symbol) {
    case "BTC-USD": return "bitcoin";
    case "ETH-USD": return "ethereum";
    case "SOL-USD": return "solana";
    case "TRX-USD": return "tron";
    case "USDC-USD":
    case "USDT-USD":
      if (network === "tron") return "tron";
      if (network === "solana") return "solana";
      return "ethereum";
    default: return "ethereum";
  }
}

export const Deposit = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [step, setStep] = useState<Step>("currency");
  const [selectedCrypto, setSelectedCrypto] = useState<CryptoType | null>(null);
  const [selectedNetwork, setSelectedNetwork] = useState<NetworkType>("mainnet");
  const [amount, setAmount] = useState("");
  const [copied, setCopied] = useState(false);
  const [_depositId, setDepositId] = useState<string | null>(null);
  const [walletAddress, setWalletAddress] = useState<string | null>(null);
  const [depositExpired, setDepositExpired] = useState(false);
  const [timeLeft, setTimeLeft] = useState(300); // 5 minutes in seconds
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // 5-minute countdown when on confirm step
  useEffect(() => {
    if (step !== "confirm") {
      if (timerRef.current) clearInterval(timerRef.current);
      return;
    }
    setDepositExpired(false);
    setTimeLeft(300);
    timerRef.current = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          if (timerRef.current) clearInterval(timerRef.current);
          setDepositExpired(true);
          hapticFeedback.error();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [step]);

  const { data: dashboardData } = useQuery(GET_DASHBOARD_DATA, {
    variables: { userId: user?.id },
    skip: !user?.id,
  });

  const [fetchPricing, { data: pricingData, loading: pricingLoading, error: pricingError }] =
    useLazyQuery(GET_CRYPTO_PRICING);

  const [makeDeposit, { loading: depositLoading }] = useMutation(MAKE_CRYPTO_DEPOSIT);

  const [fetchWallets] = useLazyQuery(GET_USER_WALLETS);

  const companyId = dashboardData?.users_by_pk?.company_id;
  const virtualCards = (dashboardData?.virtual_cards || []).filter((c: any) => c.status === "ACTIVE" || c.status === "active");
  const physicalCards = (dashboardData?.physical_cards || []).filter((c: any) => c.status === "active");
  const allCards = [
    ...physicalCards.map((c: any) => ({ id: c.id, name: c.card_name || "Physical Card", lastFour: c.card_number_last_four, type: "physical" as const })),
    ...virtualCards.map((c: any) => ({ id: c.id, name: c.card_name || "Virtual Card", lastFour: c.card_number_last_four, type: "virtual" as const })),
  ];
  const [selectedCardId, setSelectedCardId] = useState<string | null>(null);

  // Auto-select first card when data loads
  useEffect(() => {
    if (allCards.length > 0 && !selectedCardId) {
      setSelectedCardId(allCards[0].id);
    }
  }, [allCards.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectedCard = allCards.find(c => c.id === selectedCardId) || allCards[0];
  const isVirtual = selectedCard?.type === "virtual";

  // Back button navigation between steps
  useEffect(() => {
    const goBack = () => {
      switch (step) {
        case "network":
          setStep("currency");
          break;
        case "card":
          if (selectedCrypto === "USDC-USD" || selectedCrypto === "USDT-USD") {
            setStep("network");
          } else {
            setStep("currency");
          }
          break;
        case "amount":
          setStep("card");
          break;
        case "confirm":
          setStep("amount");
          break;
        default:
          navigate("/wallet");
      }
    };
    return enableBackButton(goBack);
  }, [step, selectedCrypto, allCards.length, navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch unit price (1 unit of crypto → USD) when currency is selected
  const fetchUnitPrice = useCallback(
    (symbol: CryptoType) => {
      if (companyId) {
        fetchPricing({
          variables: {
            input: {
              symbol,
              amount: "1",
              companyId,
            },
          },
        });
      }
    },
    [companyId, fetchPricing]
  );

  const nextAfterNetwork = () => {
    setStep("card");
  };

  const handleCurrencySelect = (symbol: CryptoType) => {
    hapticFeedback.selectionChanged();
    setSelectedCrypto(symbol);
    setAmount("");
    fetchUnitPrice(symbol);

    // USDC/USDT need network selection; others skip ahead
    if (symbol === "USDC-USD" || symbol === "USDT-USD") {
      setStep("network");
    } else {
      setSelectedNetwork("mainnet");
      nextAfterNetwork();
    }
  };

  const handleNetworkSelect = (network: NetworkType) => {
    hapticFeedback.selectionChanged();
    setSelectedNetwork(network);
    nextAfterNetwork();
  };

  const handleAmountChange = (value: string) => {
    // Allow only numbers and one decimal point
    if (/^\d*\.?\d*$/.test(value)) {
      setAmount(value);
    }
  };

  const [depositError, setDepositError] = useState<string | null>(null);

  const handleSubmit = async () => {
    setDepositError(null);

    if (!selectedCrypto || !amount || !user?.id) {
      setDepositError("Missing required data. Please go back and try again.");
      return;
    }

    if (!companyId) {
      setDepositError("No company assigned to your account. Contact support.");
      return;
    }

    const cryptoAmt = isStablecoin ? usdAmount : cryptoNeeded;
    if (cryptoAmt <= 0) {
      setDepositError("Unable to calculate crypto amount. Please try again.");
      return;
    }

    const cryptoAmtStr = isStablecoin
      ? cryptoAmt.toFixed(2)
      : cryptoAmt.toFixed(8);

    const confirmed = await showConfirm(
      `Deposit $${usdAmount.toFixed(2)} by sending ${cryptoAmtStr} ${CURRENCIES.find((c) => c.symbol === selectedCrypto)?.icon}?`
    );
    if (!confirmed) return;

    try {
      // Create deposit
      const { data: depositData } = await makeDeposit({
        variables: {
          input: {
            companyId,
            userId: user.id,
            cryptoType: selectedCrypto,
            cryptoAmount: cryptoAmtStr,
            network: selectedNetwork,
            ...(selectedCard && isVirtual && { virtualCardId: selectedCard.id }),
            ...(selectedCard && !isVirtual && { physicalCardId: selectedCard.id }),
          },
        },
      });

      if (!depositData?.makeCryptoDeposit?.success) {
        const errMsg = depositData?.makeCryptoDeposit?.error?.message || "Deposit failed";
        setDepositError(errMsg);
        hapticFeedback.error();
        return;
      }

      setDepositId(depositData.makeCryptoDeposit.data.depositId);

      // Fetch wallet addresses
      const { data: walletsData } = await fetchWallets({
        variables: { input: { userId: user.id } },
      });

      if (walletsData?.getUserWallets?.success) {
        const chain = symbolToChain(selectedCrypto, selectedNetwork);
        const wallet = walletsData.getUserWallets.wallets?.find(
          (w: { chain: string }) => w.chain === chain
        );
        if (wallet) {
          setWalletAddress(wallet.address);
        }
      }

      hapticFeedback.success();
      setStep("confirm");
    } catch (err) {
      const errMsg = err instanceof Error ? err.message : "Something went wrong";
      setDepositError(errMsg);
      hapticFeedback.error();
    }
  };

  const handleCopy = async () => {
    if (!walletAddress) return;
    try {
      await navigator.clipboard.writeText(walletAddress);
      setCopied(true);
      hapticFeedback.light();
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // fallback
    }
  };

  const pricing = pricingData?.getCryptoPricingEstimate;
  const isStablecoin = selectedCrypto === "USDC-USD" || selectedCrypto === "USDT-USD";
  const cryptoLabel = CURRENCIES.find((c) => c.symbol === selectedCrypto)?.icon || "";

  // Unit price = USD value of 1 crypto unit (from pricing API)
  const unitPrice = pricing?.success ? Number(pricing.data.usdPrice) : (isStablecoin ? 1 : 0);
  // Compute how much crypto the user needs to send for their USD amount
  const usdAmount = parseFloat(amount) || 0;
  const cryptoNeeded = unitPrice > 0 ? usdAmount / unitPrice : 0;
  const networkLabel = selectedNetwork === "solana" ? "Solana" : selectedNetwork === "tron" ? "Tron" : "Ethereum";

  return (
    <PageContainer>
      <GradientHeader size="sm">
        <p className="font-mono text-[11px] uppercase tracking-[0.12em] text-muted mb-1">
          Deposit crypto
        </p>
        <p className="font-display text-2xl font-semibold text-ink tracking-[-0.02em]">
          {step === "currency" && "Select currency"}
          {step === "network" && "Select network"}
          {step === "card" && "Select card"}
          {step === "amount" && "Enter USD amount"}
          {step === "confirm" && "Deposit created"}
        </p>
      </GradientHeader>

      <div className="px-4 mt-4 space-y-4">
        {/* Step 1: Currency Selection */}
        {step === "currency" && (
          <SectionCard title="Choose Cryptocurrency">
            <div className="grid grid-cols-2 gap-3 pt-2">
              {CURRENCIES.map((c) => (
                <button
                  key={c.symbol}
                  onClick={() => handleCurrencySelect(c.symbol)}
                  className="flex flex-col items-center gap-2 p-4 rounded-xl border border-hairline hover:border-accent transition-all"
                >
                  <div className="w-12 h-12 rounded-full bg-ground border border-hairline flex items-center justify-center">
                    <span className="font-mono text-xs font-semibold text-ink">
                      {c.icon}
                    </span>
                  </div>
                  <span className="text-sm font-medium text-ink">
                    {c.label}
                  </span>
                </button>
              ))}
            </div>
          </SectionCard>
        )}

        {/* Step 2: Network Selection (USDC/USDT only) */}
        {step === "network" && (
          <SectionCard title="Choose Network">
            <div className="space-y-3 pt-2">
              {NETWORKS.map((n) => (
                <button
                  key={n.value}
                  onClick={() => handleNetworkSelect(n.value)}
                  className="flex items-center justify-between w-full p-4 rounded-xl border border-hairline hover:border-accent transition-all"
                >
                  <div>
                    <p className="font-medium text-ink text-sm">
                      {n.label}
                    </p>
                    <p className="text-xs text-muted mt-0.5">
                      {n.description}
                    </p>
                  </div>
                  <ChevronRight size={16} className="text-muted/60" />
                </button>
              ))}
            </div>
          </SectionCard>
        )}

        {/* Step: Card Selection */}
        {step === "card" && (
          <SectionCard title="Which card to top up?">
            <div className="space-y-2 pt-1">
              {allCards.length === 0 ? (
                <button
                  onClick={() => {
                    hapticFeedback.selectionChanged();
                    setStep("amount");
                  }}
                  className="flex items-center justify-between w-full p-4 rounded-xl border border-accent transition-all"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center text-xs font-bold bg-card-obj text-[#9DBDB6]">
                      +
                    </div>
                    <div className="text-left">
                      <p className="text-sm font-medium text-ink">New virtual card</p>
                      <p className="text-xs text-muted">Minted automatically with your deposit</p>
                    </div>
                  </div>
                  <ChevronRight size={16} className="text-muted/60" />
                </button>
              ) : (
                allCards.map((card) => (
                  <button
                    key={card.id}
                    onClick={() => {
                      setSelectedCardId(card.id);
                      hapticFeedback.selectionChanged();
                      setStep("amount");
                    }}
                    className={`flex items-center justify-between w-full p-4 rounded-xl border transition-all ${
                      selectedCardId === card.id
                        ? "border-accent"
                        : "border-hairline"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl flex items-center justify-center font-mono text-xs font-bold bg-card-obj text-[#9DBDB6]">
                        {card.type === "physical" ? "P" : "V"}
                      </div>
                      <div className="text-left">
                        <p className="text-sm font-medium text-ink">{card.name}</p>
                        <p className="font-mono text-[11px] text-muted">
                          {card.type === "physical" ? "PHYSICAL" : "VIRTUAL"}
                          {card.lastFour ? ` · ••${card.lastFour}` : ""}
                        </p>
                      </div>
                    </div>
                    <ChevronRight size={16} className="text-muted/60" />
                  </button>
                ))
              )}
            </div>
          </SectionCard>
        )}

        {/* Step: Amount Entry */}
        {step === "amount" && (
          <>
            <SectionCard>
              <div className="pt-2">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-8 h-8 rounded-full bg-ground border border-hairline flex items-center justify-center">
                    <span className="font-mono text-xs font-bold text-ink">$</span>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-ink">
                      USD via {cryptoLabel}
                    </p>
                    {(selectedCrypto === "USDC-USD" ||
                      selectedCrypto === "USDT-USD") && (
                      <p className="font-mono text-[10px] text-muted uppercase">
                        on {networkLabel}
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex items-center justify-center py-4">
                  <span className="money text-3xl font-semibold text-ink">$</span>
                  <input
                    type="text"
                    inputMode="decimal"
                    placeholder="0.00"
                    value={amount}
                    onChange={(e) => handleAmountChange(e.target.value)}
                    className="money text-3xl font-semibold bg-transparent outline-none text-ink placeholder-muted/40 w-[calc(1ch*var(--len,3)+1ch)]"
                    style={{ "--len": Math.max(amount.length, 3) } as React.CSSProperties}
                    autoFocus
                  />
                </div>
              </div>
            </SectionCard>

            {/* Fee breakdown */}
            {usdAmount > 0 && unitPrice > 0 && (
              <SectionCard title="Conversion">
                <div className="pt-1">
                  <div className="receipt-row">
                    <span className="text-muted">deposit</span>
                    <span className="text-ink">${usdAmount.toFixed(2)}</span>
                  </div>
                  {!isStablecoin && (
                    <div className="receipt-row">
                      <span className="text-muted">{cryptoLabel} price</span>
                      <span className="text-ink">${unitPrice.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="receipt-row total">
                    <span className="text-ink">you send</span>
                    <span className="text-ink">
                      {isStablecoin
                        ? `${usdAmount.toFixed(2)} ${cryptoLabel}`
                        : `${cryptoNeeded.toFixed(8)} ${cryptoLabel}`}
                    </span>
                  </div>
                </div>
              </SectionCard>
            )}

            {pricingLoading && (
              <div className="text-center py-2">
                <div className="inline-block w-5 h-5 border-2 border-accent border-t-transparent rounded-full animate-spin" />
              </div>
            )}

            {pricingError && !pricingLoading && (
              <p className="text-xs text-error text-center">
                Unable to fetch pricing. {isStablecoin ? "You can still proceed." : "Please try again."}
              </p>
            )}

            {usdAmount > 0 && usdAmount < MINIMUM_DEPOSIT_USD && (
              <p className="text-xs text-error text-center">
                Minimum deposit is ${MINIMUM_DEPOSIT_USD}
              </p>
            )}

            {depositError && (
              <p className="text-xs text-error text-center">{depositError}</p>
            )}

            <button
              onClick={handleSubmit}
              disabled={
                !amount ||
                usdAmount <= 0 ||
                usdAmount < MINIMUM_DEPOSIT_USD ||
                (unitPrice <= 0 && !pricingLoading) ||
                depositLoading
              }
              className="w-full py-3.5 bg-ink text-ground rounded-xl font-semibold text-sm disabled:opacity-40 disabled:cursor-not-allowed active:scale-[0.98] transition-transform"
            >
              {depositLoading ? "Creating…" : "Create deposit"}
            </button>

          </>
        )}

        {/* Step 4: Confirmation + QR Code */}
        {step === "confirm" && walletAddress && (
          <>
            {/* Countdown timer */}
            <div className={`flex items-center justify-center gap-2 py-2 px-4 rounded-xl font-mono text-sm bg-surface border ${
              depositExpired
                ? "text-error border-error/40"
                : timeLeft <= 60
                  ? "text-warning border-warning/40"
                  : "text-muted border-hairline"
            }`}>
              <Clock size={14} />
              {depositExpired
                ? "PRICING EXPIRED"
                : `EXPIRES IN ${Math.floor(timeLeft / 60)}:${String(timeLeft % 60).padStart(2, "0")}`
              }
            </div>

            {depositExpired ? (
              /* Expired state */
              <>
                <SectionCard>
                  <div className="flex flex-col items-center py-8 text-center">
                    <div className="w-14 h-14 rounded-full bg-ground border border-error/30 flex items-center justify-center mb-4">
                      <AlertTriangle size={24} className="text-error" />
                    </div>
                    <p className="text-base font-semibold text-ink mb-1">Deposit expired</p>
                    <p className="text-sm text-muted max-w-[240px]">
                      The pricing for this deposit has expired. Create a new deposit to get an updated rate.
                    </p>
                  </div>
                </SectionCard>

                <button
                  onClick={() => { setStep("amount"); setWalletAddress(null); setDepositExpired(false); }}
                  className="w-full py-3.5 bg-ink text-ground rounded-xl font-semibold text-sm active:scale-[0.98] transition-transform"
                >
                  Create new deposit
                </button>
              </>
            ) : (
              /* Active deposit */
              <>
                {/* Mint order — the receipt for what's being made */}
                <div className="bg-surface rounded-xl border border-hairline p-5">
                  <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.12em] text-muted mb-2">
                    Mint order
                  </p>
                  <div className="receipt-row">
                    <span className="text-muted">you send</span>
                    <span className="text-ink">
                      {isStablecoin
                        ? `${usdAmount.toFixed(2)} ${cryptoLabel}`
                        : `${cryptoNeeded.toFixed(8)} ${cryptoLabel}`}
                    </span>
                  </div>
                  <div className="receipt-row total">
                    <span className="text-ink">loaded to card</span>
                    <span className="text-ink">${usdAmount.toFixed(2)}</span>
                  </div>
                  <p className="font-mono text-[10px] text-muted mt-2">
                    {!isStablecoin && unitPrice > 0
                      ? `1 ${cryptoLabel} = $${unitPrice.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} · ${networkLabel.toUpperCase()}`
                      : `ON ${networkLabel.toUpperCase()}`}
                  </p>
                </div>

                {/* QR Code */}
                <SectionCard>
                  <div className="flex flex-col items-center py-3">
                    <p className="font-mono text-[10px] uppercase tracking-[0.12em] text-muted font-semibold mb-3">
                      Scan to send
                    </p>
                    <div className="bg-white p-2.5 rounded-xl">
                      <QRCodeSVG
                        value={walletAddress}
                        size={160}
                        level="M"
                        includeMargin={false}
                        bgColor="transparent"
                      />
                    </div>
                  </div>
                </SectionCard>

                {/* Wallet address */}
                <SectionCard title="Wallet address">
                  <button
                    onClick={handleCopy}
                    className="flex items-center gap-3 w-full p-3 rounded-xl bg-ground border border-hairline active:bg-hairline/40 transition-colors"
                  >
                    <p className="flex-1 text-[11px] font-mono text-ink break-all text-left leading-relaxed">
                      {walletAddress}
                    </p>
                    <div className="flex items-center gap-1 flex-shrink-0 px-2 py-1 rounded-lg bg-surface border border-hairline">
                      {copied ? (
                        <>
                          <Check size={12} className="text-success" />
                          <span className="text-[10px] font-medium text-success">Copied</span>
                        </>
                      ) : (
                        <>
                          <Copy size={12} className="text-muted" />
                          <span className="text-[10px] font-medium text-muted">Copy</span>
                        </>
                      )}
                    </div>
                  </button>
                </SectionCard>

                <button
                  onClick={() => navigate("/wallet")}
                  className="w-full py-3.5 bg-ink text-ground rounded-xl font-semibold text-sm active:scale-[0.98] transition-transform"
                >
                  Done
                </button>
              </>
            )}
          </>
        )}
      </div>
    </PageContainer>
  );
};
